import React, {
  createRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";

import { Loadable, SetterOrUpdater, useRecoilValue } from "recoil";
import { threadResultsListType, threadResultsType } from "lib/store/dashboard";
import { threadPageState } from "lib/store/thread";
import { dateAtom } from "lib/store/common";

import { Symbol } from "@atsent/components";

import { ThreadItem } from "./ThreadItem";
import { Skeleton } from "components/atom/Skeleton";

import "./ThreadList.scss";

export type TreadListProps = {
  cx?: string;
  expend?: boolean;
  asyncLoadableState: [
    Loadable<threadResultsListType>,
    SetterOrUpdater<threadResultsListType>
  ];
  currentState: [
    threadResultsType,
    React.Dispatch<React.SetStateAction<threadResultsType>>
  ];
  list: any;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setTotalPage: React.Dispatch<React.SetStateAction<number>>;
};

export const ThreadList: React.FC<TreadListProps> = ({
  cx = "",
  list,
  asyncLoadableState,
  expend = false,
  setPage,
  setTotalPage,
  currentState,
}) => {
  const date = useRecoilValue(dateAtom);
  const page = useRecoilValue(threadPageState);
  const [count, setCount] = useState(0);

  const [{ state, contents }, setList] = asyncLoadableState;
  const loader = createRef<HTMLDivElement>();

  const handleObserver = useCallback(
    async (entries) => {
      const target = entries[0];
      if (target.isIntersecting) {
        await setPage((prev) => prev + 1);
      }
    },
    // eslint-disable-next-line
    []
  );

  useEffect(() => {
    if (state !== "hasValue") {
      return;
    }
    setList(contents);
    setTotalPage(contents.total_pages);
  }, [state, setList, contents, setTotalPage, date, page]);

  useLayoutEffect(() => {
    setTotalPage(list.total_pages);
    setCount(list.count);
  }, [list, setTotalPage]);

  useEffect(() => {
    const option = {
      root: null,
      rootMargin: "20px",
      threshold: 0,
    };
    const observer = new IntersectionObserver(handleObserver, option);
    if (loader.current) observer.observe(loader.current);

    // eslint-disable-next-line
  }, [handleObserver]);

  return (
    <div className={`ThreadList ${cx}`}>
      <div className="title">
        <span className={"text"}>글타래</span>
        <div className="r">
          {state === "hasValue" && !!contents ? (
            <span className={"page"}>총 {count} 개</span>
          ) : (
            <Skeleton />
          )}
        </div>
      </div>
      <div className="scroll-wrapper">
        {list.results.length === 0 && (
          <div className="empty">
            <Symbol />
            <span>데이터가 없습니다.</span>
          </div>
        )}
        {list.results.map((data: threadResultsType) => (
          <ThreadItem
            currentState={currentState}
            data={data}
            text={data.plain_text}
            date={data.created_at}
            id={data.idx}
            key={`threadItem-${data.id}`}
            hasNumber
            expend={expend}
          />
        ))}
        <div ref={loader} />
      </div>
    </div>
  );
};
